import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import { PiClock } from 'react-icons/pi'

import { Alert } from '../../components/FbUI/Alert'
import { FbModal as Modal } from '../../components/UI/Modal'
import { DatePicker } from '../../components/FormUtils/DatePicker'
import TimePicker from '../../components/FormUtils/TimePicker'
import { getApiErrorDetail } from '../../utils/getApiErrorDetail'
import { useScheduleCampaignLaunch } from 'services/campaigns'
import { splitZonedIso, zonedDateTimeToUtcIso } from './scheduleTime'

interface Props {
  show: boolean
  onHide: () => void
  bisonCampaignId: number
  campaignId: number
  scheduleTimezone: string
  initialScheduledAt?: string | null
}

export function ScheduleLaunchModal({
  show,
  onHide,
  bisonCampaignId,
  campaignId,
  scheduleTimezone,
  initialScheduledAt,
}: Props) {
  // Seed `time` with the same default the TimePicker shows ("09:00 AM") so
  // canSubmit reflects the visible state. Previously we relied on the
  // picker pushing its default upward on mount, but that round-trip left
  // the Schedule button disabled until the user reselected a time.
  const initial = useMemo(
    () =>
      initialScheduledAt
        ? splitZonedIso(initialScheduledAt, scheduleTimezone)
        : { date: '', time: '09:00' },
    [initialScheduledAt, scheduleTimezone]
  )

  const [date, setDate] = useState<string>(initial.date)
  const [time, setTime] = useState<string>(initial.time)

  const scheduleMutation = useScheduleCampaignLaunch()

  const scheduledIso = useMemo(() => {
    if (!date || !time) return null
    try {
      return zonedDateTimeToUtcIso(date, time, scheduleTimezone)
    } catch {
      return null
    }
  }, [date, time, scheduleTimezone])

  const isInFuture = useMemo(() => {
    if (!scheduledIso) return false
    return new Date(scheduledIso).getTime() > Date.now()
  }, [scheduledIso])

  const canSubmit = Boolean(date && time && isInFuture)

  async function handleSubmit() {
    if (!scheduledIso) return
    try {
      await scheduleMutation.mutateAsync({
        bison_campaign_id: bisonCampaignId,
        campaign_id: campaignId,
        data: { scheduled_launch_at: scheduledIso },
      })
      toast.success('Campaign scheduled for launch')
      onHide()
    } catch (err: unknown) {
      toast.error(getApiErrorDetail(err, 'Failed to schedule campaign launch'))
    }
  }

  return (
    <Modal
      open={show}
      onOpenChange={(open: boolean) => !open && onHide()}
      title="Schedule launch"
      description="Pick a date and time. Your campaign will launch automatically at that moment."
      onAccept={handleSubmit}
      acceptButtonText="Schedule"
      blockAccept={!canSubmit}
      loading={scheduleMutation.isPending}
    >
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-fg">Date</label>
          <DatePicker
            value={date}
            onChange={(v) => setDate(v ?? '')}
            allowTyping
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-fg">Time</label>
          <TimePicker value={time} onChange={setTime} />
        </div>
        <Alert
          color="info"
          icon={PiClock}
          description={
            <>
              Times shown in <strong>{scheduleTimezone}</strong> (your send
              schedule).
            </>
          }
        />
        {date && time && !isInFuture && (
          <Alert
            color="danger"
            description="Scheduled time must be in the future."
          />
        )}
      </div>
    </Modal>
  )
}
